(() => {
  "use strict";

  const SELECTORS = {
    widget: "[data-ck-cause-widget]",
    config: "[data-ck-cause-config]",
    dialog: "[data-ck-cause-dialog]",
    open: "[data-ck-cause-open]",
    close: "[data-ck-cause-close]",
    save: "[data-ck-cause-save]",
    clear: "[data-ck-cause-clear]",
    status: "[data-ck-cause-status]",
    selection: "[data-ck-cause-selection]",
    selectedName: "[data-ck-cause-selected-name]",
    estimate: "[data-ck-cause-estimate]",
    barCharity: "[data-ck-cause-bar-charity]",
    compactOpen: "[data-ck-cause-compact-open]",
    compactToggle: "[data-ck-cause-toggle]",
    rebuyCheckout: '[data-rebuy-component-id="checkout"]',
    rebuyHost: "[data-ck-cause-rebuy-host]",
    rebuySource: "[data-ck-cause-rebuy-source]",
  };

  const state = {
    pendingSave: null,
    lastFocused: new WeakMap(),
    bypassForms: new WeakSet(),
    syncTimer: null,
    rebuyMountTimer: null,
  };

  const localeUrl = (path) => {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.replace(/\/?$/, "/")}${path.replace(/^\//, "")}`;
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const readConfig = (widget) => {
    try {
      return JSON.parse(widget.querySelector(SELECTORS.config)?.textContent || "{}");
    } catch (_error) {
      return null;
    }
  };

  const setStatus = (widget, message = "", status = "") => {
    const element = widget.querySelector(SELECTORS.status);
    if (!element) return;
    element.textContent = message;
    if (status) element.dataset.state = status;
    else delete element.dataset.state;
  };

  const setBusy = (busy) => {
    document.querySelectorAll(SELECTORS.widget).forEach((widget) => {
      widget.setAttribute("aria-busy", String(busy));
      widget
        .querySelectorAll(
          `${SELECTORS.save}, ${SELECTORS.clear}, ${SELECTORS.open}, ${SELECTORS.compactOpen}, ${SELECTORS.compactToggle}`,
        )
        .forEach((button) => {
          button.disabled = busy;
        });
    });
  };

  const setSelection = (widget, charityId, announce = false) => {
    const config = readConfig(widget);
    if (!config) return;

    const charity = config.charities.find((item) => item.id === charityId);
    const selection = widget.querySelector(SELECTORS.selection);
    const selectedName = widget.querySelector(SELECTORS.selectedName);
    const estimate = widget.querySelector(SELECTORS.estimate);
    const barCharity = widget.querySelector(SELECTORS.barCharity);
    const compactToggle = widget.querySelector(SELECTORS.compactToggle);
    const openButton = widget.querySelector(SELECTORS.open);
    const clearButton = widget.querySelector(SELECTORS.clear);

    widget.dataset.ckCauseSelected = charity?.id || "";
    config.selectedCharity = charity?.id || "";

    widget.querySelectorAll('input[name="ck-cause-charity-choice"]').forEach((radio) => {
      radio.checked = radio.value === charity?.id;
    });

    if (charity) {
      selection.hidden = false;
      selectedName.textContent = charity.name;
      estimate.textContent = `Estimated Carolina K allocation: ${formatMoney(config.estimateCents, config.currency)}`;
      openButton.textContent = "Change organization";
      barCharity.textContent = charity.name;
      compactToggle.setAttribute("aria-pressed", "true");
      compactToggle.setAttribute("aria-label", "Remove charity selection");
      clearButton.hidden = false;
      if (announce) setStatus(widget);
    } else {
      selection.hidden = true;
      selectedName.textContent = "";
      estimate.textContent = "";
      openButton.textContent = "Choose an organization";
      barCharity.textContent = "No charity selected";
      compactToggle.setAttribute("aria-pressed", "false");
      compactToggle.setAttribute("aria-label", "Choose a charity");
      clearButton.hidden = true;
      if (announce) setStatus(widget);
    }
  };

  const updateAllWidgets = (charityId, announce = false) => {
    document.querySelectorAll(SELECTORS.widget).forEach((widget) => {
      setSelection(widget, charityId, announce);
    });
  };

  const closeDialog = (widget) => {
    const dialog = widget.querySelector(SELECTORS.dialog);
    if (!dialog) return;

    if (typeof dialog.close === "function" && dialog.open) dialog.close();
    else dialog.removeAttribute("open");

    state.lastFocused.get(dialog)?.focus();
  };

  const cartAttributesFor = (charityId) => ({
    cause_allocation_charity: charityId || "",
  });

  const updateCartAttributes = async (attributes) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 6000);

    try {
      const response = await fetch(localeUrl("cart/update.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ attributes }),
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const saveSelection = async (widget, charityId) => {
    const config = readConfig(widget);
    if (!config || state.pendingSave) return;

    const validChoice = charityId === "" || config.charities.some((item) => item.id === charityId);
    if (!validChoice) {
      setStatus(widget, "Choose one of the listed organizations.", "error");
      return;
    }

    setStatus(widget);
    setBusy(true);

    const request = updateCartAttributes(cartAttributesFor(charityId));
    state.pendingSave = request;

    try {
      await request;
      updateAllWidgets(charityId, true);
      closeDialog(widget);
      try {
        localStorage.setItem("ck-cause-selection-updated", String(Date.now()));
      } catch (_error) {
        // Cross-tab synchronization is optional; the cart save already succeeded.
      }
      document.dispatchEvent(
        new CustomEvent("ck-cause:selection-saved", {
          detail: { charityId },
        }),
      );
    } catch (_error) {
      setStatus(
        widget,
        "Your selection was not saved. Please retry, or continue to checkout without a selection.",
        "error",
      );
    } finally {
      state.pendingSave = null;
      setBusy(false);
    }
  };

  const initializeWidget = (widget) => {
    if (widget.dataset.ckCauseInitialized === "true") return;
    const config = readConfig(widget);
    if (!config || !Array.isArray(config.charities) || config.charities.length !== 4) return;

    widget.dataset.ckCauseInitialized = "true";
    const dialog = widget.querySelector(SELECTORS.dialog);
    const openButton = widget.querySelector(SELECTORS.open);
    const compactOpenButton = widget.querySelector(SELECTORS.compactOpen);
    const compactToggle = widget.querySelector(SELECTORS.compactToggle);
    const saveButton = widget.querySelector(SELECTORS.save);
    const clearButton = widget.querySelector(SELECTORS.clear);

    // A fresh cart has no saved charity and must always begin unchecked.
    const savedCharityId = widget.dataset.ckCauseSelected || config.selectedCharity || "";
    setSelection(widget, savedCharityId);

    const openDialog = () => {
      state.lastFocused.set(dialog, document.activeElement);
      setStatus(widget);
      const currentChoice = widget.dataset.ckCauseSelected;
      widget.querySelectorAll('input[name="ck-cause-charity-choice"]').forEach((radio) => {
        radio.checked = radio.value === currentChoice;
      });
      saveButton.disabled = !widget.querySelector('input[name="ck-cause-charity-choice"]:checked');

      if (typeof dialog?.showModal === "function") dialog.showModal();
      else dialog?.setAttribute("open", "");
    };

    openButton?.addEventListener("click", openDialog);
    compactOpenButton?.addEventListener("click", openDialog);
    compactToggle?.addEventListener("click", () => {
      if (widget.dataset.ckCauseSelected) saveSelection(widget, "");
      else openDialog();
    });

    widget.querySelectorAll(SELECTORS.close).forEach((button) => {
      button.addEventListener("click", () => closeDialog(widget));
    });

    dialog?.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeDialog(widget);
    });

    widget.querySelectorAll('input[name="ck-cause-charity-choice"]').forEach((radio) => {
      radio.addEventListener("change", () => {
        saveButton.disabled = false;
      });
    });

    saveButton?.addEventListener("click", () => {
      const choice = widget.querySelector('input[name="ck-cause-charity-choice"]:checked');
      if (choice) saveSelection(widget, choice.value);
    });

    clearButton?.addEventListener("click", () => saveSelection(widget, ""));
  };

  const initializeAll = () => {
    document.querySelectorAll(SELECTORS.widget).forEach((widget) => {
      if (!widget.closest(SELECTORS.rebuySource)) initializeWidget(widget);
    });
  };

  const mountRebuyWidget = async () => {
    const checkout = document.querySelector(SELECTORS.rebuyCheckout);
    if (!checkout) return;

    let cart;
    try {
      const response = await fetch(localeUrl("cart.js"), {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      cart = await response.json();
    } catch (_error) {
      return;
    }

    const currentHost = document.querySelector(SELECTORS.rebuyHost);
    if (Number(cart.item_count || 0) <= 0) {
      currentHost?.remove();
      return;
    }

    if (currentHost?.isConnected && currentHost.parentElement === checkout.parentElement) return;
    currentHost?.remove();

    const sourceWidget = document.querySelector(
      `${SELECTORS.rebuySource} ${SELECTORS.widget}`,
    );
    if (!sourceWidget) return;

    const widgetClone = sourceWidget.cloneNode(true);

    const host = document.createElement("div");
    host.className = "ck-cause-widget-wrapper ck-cause-widget-wrapper--rebuy";
    host.dataset.ckCauseRebuyHost = "";
    host.append(widgetClone);
    checkout.before(host);
    initializeAll();
  };

  const scheduleRebuyMount = () => {
    window.clearTimeout(state.rebuyMountTimer);
    state.rebuyMountTimer = window.setTimeout(mountRebuyWidget, 100);
  };

  const syncSelectionFromCart = async () => {
    if (state.pendingSave) return;
    try {
      const response = await fetch(localeUrl("cart.js"), {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      updateAllWidgets(cart.attributes?.cause_allocation_charity || "");
    } catch (_error) {
      // Synchronization is best-effort and must never interfere with checkout.
    }
  };

  const scheduleCartSync = () => {
    window.clearTimeout(state.syncTimer);
    state.syncTimer = window.setTimeout(syncSelectionFromCart, 150);
  };

  document.addEventListener(
    "submit",
    (event) => {
      if (!state.pendingSave) return;
      const form = event.target;
      const submitter = event.submitter;
      const isCheckout =
        submitter?.name === "checkout" ||
        submitter?.id === "checkout" ||
        submitter?.matches?.('[name="checkout"]');
      if (!isCheckout || state.bypassForms.has(form)) {
        state.bypassForms.delete(form);
        return;
      }

      event.preventDefault();

      Promise.race([
        state.pendingSave.catch(() => undefined),
        new Promise((resolve) => window.setTimeout(resolve, 6500)),
      ]).finally(() => {
        state.bypassForms.add(form);
        if (typeof form.requestSubmit === "function") form.requestSubmit(submitter);
        else form.submit();
      });
    },
    true,
  );

  window.addEventListener("storage", (event) => {
    if (event.key === "ck-cause-selection-updated") scheduleCartSync();
  });
  window.addEventListener("focus", scheduleCartSync);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleCartSync();
  });

  const observer = new MutationObserver(() => {
    window.queueMicrotask(initializeAll);
    scheduleRebuyMount();
  });

  const start = () => {
    initializeAll();
    scheduleRebuyMount();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
